import React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Icon } from "@/components/ui/Icon";
import { Label } from "@/components/ui/form/Label";

const selectVariants = cva(
  "appearance-none border w-full rounded-full px-5 py-2.5 text-base font-weight-regular transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-2 cursor-pointer pr-10",
  {
    variants: {
      color: {
        purple: "bg-transparent border-purple text-black focus:outline-purple",
        yellow: "bg-transparent border-yellow text-black focus:outline-yellow",
        dark: "bg-black/30 backdrop-blur-md text-white border-white/60 focus:outline-yellow placeholder:text-white/70",
      },
    },
    defaultVariants: {
      color: "purple",
    },
  }
);

const selectIconVariants = cva("w-8 h-8 transition-colors duration-200", {
  variants: {
    color: {
      purple: "text-purple",
      yellow: "text-yellow",
      dark: "text-yellow",
    },
  },
  defaultVariants: {
    color: "purple",
  },
});

export interface SelectProps
  extends
    Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "color">,
    VariantProps<typeof selectVariants> {
  /** Error message displayed below the select */
  error?: string;
  /** Label for the select */
  label?: string;
  /** Additional CSS classes */
  className?: string;
  /** Options for the select */
  options?: Array<{ label: string; value: string }>;
}

/**
 * Select component for dropdown selection.
 * Supports color variants, custom options, and error states.
 * Designed to work with reactive values for interactive filtering.
 */
export const Select: React.FC<SelectProps> = ({
  color = "purple",
  error,
  label,
  className,
  id,
  options = [],
  children,
  ...props
}) => {
  const selectId = id || props.name;

  return (
    <div className="flex flex-col gap-xs w-full relative">
      {label && (
        <Label
          htmlFor={selectId}
          color={color || "purple"}
          disabled={props.disabled}
          className="mx-4"
        >
          {label}
        </Label>
      )}
      <div className="relative w-full">
        <select
          id={selectId}
          className={selectVariants({
            color,
            className: `${error ? "border-red-500! focus:outline-red-500!" : ""} ${className}`,
          })}
          aria-invalid={!!error}
          aria-describedby={error && selectId ? `${selectId}-error` : undefined}
          aria-label={label || props["aria-label"]}
          {...props}
        >
          {children ||
            options.map((option) => (
              <option
                key={option.value}
                value={option.value}
                className={
                  color === "dark"
                    ? "bg-zinc-900 text-white"
                    : "bg-white text-black"
                }
              >
                {option.label}
              </option>
            ))}
        </select>
        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
          <Icon
            name="chevron-down-2"
            className={selectIconVariants({ color })}
          />
        </div>
      </div>
      {error && (
        <span
          id={selectId ? `${selectId}-error` : undefined}
          className="text-red-500 text-sm ml-5"
          role="alert"
        >
          {error}
        </span>
      )}
    </div>
  );
};
